import {
  readStored,
  readStoredObject,
  removeStored,
  rootKeys,
  writeStored,
} from '../core/utils/storage';

/** Single entry holding the local dashboard state, instead of one flat entry per subject. */
export const DASHBOARD_LS_KEY = 'dashboard';

const LEGACY_ID_KEY = 'DASHBOARD_KEY_ID';
const LEGACY_SCROLL_KEY = 'DASHBOARD_SCROLL_TOP';
const LEGACY_VARIABLES_PREFIX = 'DASHBOARD_KEY_VARIABLES_';

/** Variables of a dashboard, serialized as `var-<type>-<id>` / value pairs. */
export type StoredVariables = Record<string, number | string>;

export interface DashboardScroll {
  id: number;
  scrollTop: number;
}

export interface DashboardStorage {
  /** Last dashboard displayed, reopened when the url carries no id. */
  id?: number;
  scroll?: DashboardScroll;
  /** Variables of every dashboard visited, indexed by dashboard id. */
  variables?: Record<number, StoredVariables>;
}

export function getStoredDashboardId(): number | null {
  return readStorage().id ?? null;
}

export function setStoredDashboardId(id: number): void {
  writeStorage({ ...readStorage(), id });
}

export function getStoredScroll(): DashboardScroll {
  return readStorage().scroll;
}

export function setStoredScroll(scroll: DashboardScroll): void {
  writeStorage({ ...readStorage(), scroll });
}

export function clearStoredScroll(): void {
  const storage = readStorage();
  delete storage.scroll;
  writeStorage(storage);
}

export function getStoredVariables(id: number): StoredVariables | null {
  return readStorage().variables?.[id] ?? null;
}

export function setStoredVariables(id: number, variables: StoredVariables): void {
  const storage = readStorage();
  writeStorage({ ...storage, variables: { ...storage.variables, [id]: variables } });
}

/** Forgets the last dashboard displayed, along with the variables of `id` when given. */
export function clearStoredDashboard(id?: number): void {
  const storage = readStorage();
  delete storage.id;
  if (id) {
    delete storage.variables?.[id];
  }
  writeStorage(storage);
}

/** Folds the legacy flat entries into the `dashboard` entry, then drops them, at startup. */
export function migrateLegacyDashboardStorage(): void {
  const storage = readStorage();
  let hasLegacyEntry = false;

  for (const key of rootKeys()) {
    if (key === LEGACY_ID_KEY) {
      storage.id = storage.id ?? readStored<number>(key);
    } else if (key === LEGACY_SCROLL_KEY) {
      storage.scroll = storage.scroll ?? readStored<DashboardScroll>(key);
    } else if (key.startsWith(LEGACY_VARIABLES_PREFIX)) {
      const id = Number(key.slice(LEGACY_VARIABLES_PREFIX.length));
      const variables = readStored<StoredVariables>(key);
      if (id && variables) {
        storage.variables = { ...storage.variables, [id]: variables };
      }
    } else {
      continue;
    }

    hasLegacyEntry = true;
    removeStored(key);
  }

  if (hasLegacyEntry) {
    writeStorage(storage);
  }
}

function readStorage(): DashboardStorage {
  return readStoredObject<DashboardStorage>(DASHBOARD_LS_KEY);
}

function writeStorage(storage: DashboardStorage): void {
  writeStored(DASHBOARD_LS_KEY, storage);
}
